import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import _ from 'lodash'
import { Copy } from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { DataTableFilter } from '@/components/DataTable/DataTableFilter'
import { Button } from '@/components/elements/button'
import { LoadError } from '@/components/LoadError'
import { StatusBadge } from '@/components/StatusBadge'
import { TextLink } from '@/components/TextLink'
import { getEntityRoles } from '@/services/entity.service'
import {
  getEntityCertificateStudents,
  removeStudentCertificate,
} from '@/services/entity_student_certificates.service'
import { UserStoreContext } from '@/stores/UserStore'
import { formatDate } from '@/utils/formatters'
import { toast, uniqueEmails } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

import EventSlideoutView from '../../Calendar/EventSlideoutView'
import RegisterForCertificateEventModal from '../Students/RegisterForCertificateEventModal'

import AddStudentToCertificateModal from './AddStudentToCertificateModal'

// Entity roles eligible to assign students to certs. Xverse instructors may
// also appear in the data; they aren't in this dropdown today, but they'll
// still render in the table.
const ASSIGNER_ROLES = ['Owner', 'Admin', 'Instructor']

const StudentTable = observer(({ certificate }) => {
  const { canManageInstructorResources, isXverseAdmin, isXverseInstructor } =
    useContext(UserStoreContext)
  // Context
  const { entityId } = useParams()
  const queryClient = useQueryClient()
  const certificateId = certificate.id

  // Mirrors the backend's can_assign_student_to_cert: Xverse certs require an
  // Xverse Admin or Xverse Instructor; facility certs only require an entity
  // manager (Owner / Admin / Instructor), which is what canManageInstructorResources
  // already gates on.
  const canAssignCourseToStudent =
    canManageInstructorResources &&
    (!certificate?.isXverseCertificate || isXverseAdmin || isXverseInstructor)

  // State
  const [sorting, setSorting] = useState([
    { id: 'last_name', desc: false },
    { id: 'first_name', desc: false },
  ])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const [filters, setFilters] = useState({})
  const [viewEvent, setViewEvent] = useState(null)
  const [registerForCertificate, setRegisterForCertificate] = useState(null)
  const [showAddStudentModal, setShowAddStudentModal] = useState(false)

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [filters])

  // Source for the "Assigned by" filter dropdown — entity managers at this
  // entity. Limited to a single large page since we don't expect more than
  // a few dozen managers per facility.
  const { data: assignerRoles } = useQuery({
    queryKey: ['accounts', 'instructors', entityId, 'manager-roles'],
    queryFn: () =>
      getEntityRoles({
        entityId,
        pagination: { pageSize: 1000, pageIndex: 0 },
        filters: { roles: ASSIGNER_ROLES },
      }),
    placeholderData: keepPreviousData,
  })
  const ASSIGNER_OPTIONS = (assignerRoles?.results ?? []).map((r) => ({
    label: formatUserName(r.user),
    id: r.user.id,
  }))

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: [
      'accounts',
      'instructors',
      'certificates',
      certificateId,
      'students',
      entityId,
      sorting,
      pagination,
      filters,
    ],
    queryFn: () =>
      getEntityCertificateStudents(entityId, certificateId, {
        sorting,
        pagination,
        searchTerm: filters.search,
        filters: _.omit(filters, 'search'),
      }),
    placeholderData: keepPreviousData,
  })

  const [removingId, setRemovingId] = useState(null)
  const { mutate: removeStudent } = useMutation({
    mutationFn: (studentCertificateId) => {
      setRemovingId(studentCertificateId)
      return removeStudentCertificate(entityId, studentCertificateId)
    },
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Student removed and credit refunded', 'success')
      queryClient.invalidateQueries({
        queryKey: ['accounts', 'instructors', 'certificates', certificateId, 'students'],
      })
    },
    onSettled: () => setRemovingId(null),
  })

  const renderStatusColumn = useCallback(
    (status) => <StatusBadge status={status} size="sm" type="studentCertificate" />,
    [],
  )
  const renderNameColumn = useCallback(
    (info) => (
      <TextLink to={`/accounts/${entityId}/instructors/students/${info.row.original.student.id}`}>
        {info.getValue()}
      </TextLink>
    ),
    [entityId],
  )

  const renderTrainingColumn = useCallback(
    (info) => {
      const event = info.getValue()
      if (!event) {
        if (!canManageInstructorResources) return null
        return (
          <Button
            size="sm"
            onClick={() => {
              setRegisterForCertificate({
                name: formatUserName(info.row.original.student.user),
                studentCertificate: info.row.original,
              })
            }}
          >
            Schedule Training
          </Button>
        )
      }
      if (event.trainingStatus === 'Completed') return 'Complete'
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setViewEvent({ event, studentId: info.row.original.student.id })}
        >
          View Event
        </Button>
      )
    },
    [canManageInstructorResources],
  )

  const renderRemoveColumn = useCallback(
    ({ row }) => {
      if (!canManageInstructorResources) return null
      if (row.original.status !== 'Not Started') return null
      return (
        <ConfirmButton
          confirmTitle="Remove Student"
          confirmContent={`Are you sure you want to remove ${formatUserName(row.original.student?.user)}? Their account will be refunded.`}
          className="place-self-end"
          variant="destructive"
          size="sm"
          loading={removingId === row.original.id}
          onClick={() => removeStudent(row.original.id)}
        >
          Remove
        </ConfirmButton>
      )
    },
    [removingId, removeStudent, canManageInstructorResources],
  )

  const columnHelper = createColumnHelper()
  const columns = [
    columnHelper.accessor((row) => formatUserName(row?.student?.user), {
      header: 'Name',
      cell: renderNameColumn,
    }),
    columnHelper.accessor('student.user.email', {
      header: 'Email',
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => renderStatusColumn(getValue()),
    }),
    columnHelper.accessor('courseRecords', {
      header: 'Online Material',
      cell: ({ getValue }) => {
        const courseRecords = getValue()
        const courseRecordCount = courseRecords.length
        const completedCount = courseRecords.filter((cr) => cr.status === 'Completed').length
        if (completedCount === courseRecordCount) return 'Complete'
        return `${completedCount}/${courseRecordCount} complete`
      },
    }),
    // Self-paced certs (no training FK) have no event to schedule — omit the
    // column entirely rather than prompting instructors to Schedule Training.
    ...(certificate.training
      ? [
          columnHelper.accessor('event', {
            header: 'Training',
            cell: (info) => renderTrainingColumn(info),
          }),
        ]
      : []),
    columnHelper.accessor('expiration', {
      header: 'Cert Expiration',
    }),
    columnHelper.accessor((row) => (row.assignedBy ? formatUserName(row.assignedBy) : null), {
      id: 'assigned_by',
      header: 'Assigned By',
      cell: ({ getValue }) => getValue() ?? <span className="text-muted-foreground">—</span>,
    }),
    columnHelper.accessor('createdAt', {
      id: 'created_at',
      header: 'Assigned On',
      cell: ({ getValue }) =>
        formatDate(getValue()) || <span className="text-muted-foreground">—</span>,
    }),
    {
      id: 'remove',
      cell: renderRemoveColumn,
      size: 100,
    },
  ]

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex justify-between">
          <h2>Students</h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={!data?.results?.length}
              onClick={async () => {
                try {
                  const all = await getEntityCertificateStudents(entityId, certificateId, {
                    pagination: { pageIndex: 0, pageSize: 10000 },
                    filters: _.omit(filters, 'search'),
                    searchTerm: filters.search,
                    sorting,
                  })
                  const emails = uniqueEmails(
                    (all?.results || []).map((sc) => sc.student?.user?.email),
                  )
                  await navigator.clipboard.writeText(emails.join(', '))
                  toast(
                    `${emails.length} ${emails.length === 1 ? 'email' : 'emails'} copied to clipboard`,
                  )
                } catch {
                  toast('Failed to copy emails', 'error')
                }
              }}
            >
              <Copy className="h-4 w-4" />
              Copy Emails
            </Button>
            {canManageInstructorResources && (
              <Button
                // The modal hides students who already hold this course, and it
                // reads that list from this table. Until the roster is loaded
                // there is no list, so it would offer people who are already on
                // the course.
                disabled={!canAssignCourseToStudent || isPending || isError}
                onClick={() => setShowAddStudentModal(true)}
              >
                Assign Course to Student
              </Button>
            )}
          </div>
        </div>
        {/* The filters stay put through a failure. They drive this same
            request, so narrowing the search is a second way out of it — and
            the one that helps when the roster was too big to load. */}
        <DataTableFilter
          setFilters={setFilters}
          filterValues={filters}
          filters={[
            { type: 'input', label: 'Search for Student', id: 'search' },
            {
              type: 'select',
              label: 'Status',
              id: 'status',
              options: ['Not Started', 'In Progress', 'Issued', 'Expired'].map((status) => ({
                label: status,
                id: status,
              })),
            },
            {
              type: 'multiselect',
              label: 'Assigned By',
              id: 'assigned_by',
              options: ASSIGNER_OPTIONS,
            },
          ]}
        />
        {isError ? (
          <LoadError
            message="We could not load the students on this course."
            detail="This does not mean the course has no students — the request failed."
            onRetry={refetch}
          />
        ) : (
          <DataTable
            columns={columns}
            data={data?.results || []}
            loading={isPending}
            pagination={pagination}
            rowCount={data?.count || 0}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
          />
        )}
      </div>
      {viewEvent && (
        <EventSlideoutView
          viewEvent={viewEvent.event}
          studentId={viewEvent.studentId}
          canManage={canManageInstructorResources}
          resetAndClose={() => {
            setViewEvent(null)
          }}
        />
      )}
      {registerForCertificate && (
        <RegisterForCertificateEventModal
          studentCertificateId={registerForCertificate.studentCertificate.id}
          certificateId={registerForCertificate.studentCertificate.certificate.id}
          entityId={entityId}
          name={registerForCertificate.name}
          onClose={() => {
            setRegisterForCertificate(null)
          }}
        />
      )}
      {showAddStudentModal && (
        <AddStudentToCertificateModal
          certificate={certificate}
          setOpen={(open) => setShowAddStudentModal(open)}
          assignedStudents={data?.results}
        />
      )}
    </>
  )
})

export default StudentTable
StudentTable.propTypes = {
  certificate: PropTypes.object.isRequired,
}
